/**
 * The artist website's own content model.
 *
 * This is deliberately NOT the profile DTO. Account data (bio, artworks, contact) only *seeds*
 * initial values; after that the website's fields are independent and can be richer than what
 * the account stores.
 *
 * ⚠️ These property names are **camelCase**, and that is the one documented exception to this
 * repo's PascalCase-DTO rule. The API stores the document as an opaque `JToken` so it round-trips
 * byte-for-byte — had it been typed server-side, the PascalCase contract resolver would have
 * re-cased `siteTitle` to `SiteTitle` in transit and silently broken every lookup here. Only the
 * envelope around it (`ArtistId`, `IsPublished`, `Slug`, …) is PascalCase.
 *
 * Because the client owns the shape, new fields can be added without a backend release — the
 * server returns anything it was given.
 */

export type SizeKey = 'sm' | 'md' | 'lg' | 'full';

export type SectionType = 'cover' | 'work' | 'text' | 'imageText' | 'contact';

/**
 * The curated looks. Layout is a function of look + section type + width — there is deliberately
 * no free-form layout, which is what stops an artist producing a bad site.
 *
 * The set is spread across ground and voice rather than offering six variations of one idea:
 * three light (`gallery` minimal · `editorial` typographic · `ink` contemporary) and three
 * coloured (`studio` warm · `salon` deep gallery-wall · `bold` loud).
 */
export type SiteLook = 'gallery' | 'studio' | 'bold' | 'editorial' | 'salon' | 'ink';

/** A page is an ordered list of these — reorder, insert and delete are array operations. */
export interface Section {
  id: string;
  type: SectionType;
  width?: SizeKey;
  /**
   * Kept on the page but not shown to visitors. Absent means shown, which is what every site
   * saved before this existed must keep doing — add the key only when it is true.
   */
  hidden?: boolean;
  /**
   * `data.hiddenElements?: string[]` — keys from {@link SECTION_ELEMENTS} the artist has switched
   * off. The content stays stored, so switching an element back on restores it exactly.
   */
  data: any;
}

/**
 * Sections that are hidden rather than deleted.
 *
 * A page has exactly one of each. They used to be deletable but could not be added back — the
 * insert menu never offered them, because only the first-run scaffold creates them — so one click
 * lost an artist's header for good (2026-10-03). Deleting `contact` was worse: it holds the address
 * every enquiry button on the site sends to, so it silently switched enquiries off everywhere.
 */
export const STRUCTURAL_SECTIONS: readonly SectionType[] = ['cover', 'contact'];

export function isStructural(sec: Section | null | undefined): boolean {
  return !!sec && STRUCTURAL_SECTIONS.indexOf(sec.type) !== -1;
}

/** One part of a section the artist can switch off. `key` is the `data` field that holds it. */
export interface SectionElement {
  key: string;
  label: string;
}

/**
 * What can be switched off in each section type. The keys are `data` field names and are stored in
 * published documents — add to these lists, never rename an entry.
 */
export const SECTION_ELEMENTS: Readonly<Record<SectionType, readonly SectionElement[]>> = {
  cover: [
    { key: 'headline', label: 'Headline' },
    { key: 'subhead', label: 'Subhead' },
    { key: 'button', label: 'Button' },
    { key: 'heroImage', label: 'Image' },
    { key: 'promoHeading', label: 'Promo heading' },
    { key: 'promoBody', label: 'Promo text' },
  ],
  text: [
    { key: 'heading', label: 'Heading' },
    { key: 'body', label: 'Text' },
  ],
  imageText: [
    { key: 'image', label: 'Image' },
    { key: 'heading', label: 'Heading' },
    { key: 'body', label: 'Text' },
  ],
  work: [
    { key: 'heading', label: 'Heading' },
    { key: 'intro', label: 'Description' },
  ],
  contact: [
    { key: 'heading', label: 'Heading' },
    { key: 'intro', label: 'Intro' },
  ],
};

/** True when the artist has switched this element off. */
export function isElementHidden(sec: Section | null | undefined, key: string): boolean {
  const off = sec?.data?.hiddenElements;
  return Array.isArray(off) && off.indexOf(key) !== -1;
}

export interface SiteContent {
  look: SiteLook;
  accent: string;
  hfont: string;
  bfont: string;
  siteTitle: string;
  /**
   * How work cards present. `size` is part of the artist's design and publishes with the site —
   * a visitor does not get to re-densify someone's gallery, so the public renderer hides the
   * grid's size control and uses this instead of the per-browser preference.
   */
  card: {
    showTitle: boolean;
    showCategory: boolean;
    showPrice: boolean;
    showStatus: boolean;
    size?: 'sm' | 'md' | 'lg';
  };
  sections: Section[];
}

/**
 * An artwork as the website renders it — flattened from `ArtistProductSummary` so the renderer
 * never has to know the API DTO. `shown` is the artist's curation for Work sections and is what
 * gets persisted as `ShownIds`.
 */
export interface SiteWork {
  id: string;
  name: string;
  image: string;
  medium: string;
  category: string;
  series: string;
  dims: string;
  description: string;
  price: number;
  sold: boolean;
  consigned: boolean;
  shown: boolean;
}
